import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Card, CardBody } from '../../../styles/components/ModernCard';

// Subtle hover effect for stats container
const containerGlow = keyframes`
  0% { box-shadow: 0 10px 30px rgba(15, 23, 42, 0.1); }
  50% { box-shadow: 0 10px 30px rgba(99, 102, 241, 0.15); }
  100% { box-shadow: 0 10px 30px rgba(15, 23, 42, 0.1); }
`;

// Pulse animation for stat value
const valuePulse = keyframes`
  0% { text-shadow: 0 0 8px rgba(99, 102, 241, 0.3); }
  50% { text-shadow: 0 0 15px rgba(99, 102, 241, 0.5); }
  100% { text-shadow: 0 0 8px rgba(99, 102, 241, 0.3); }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.75rem;
  margin-bottom: 2.5rem;
  padding: 1.5rem;
  position: relative;
  border-radius: 20px;
  animation: ${containerGlow} 8s infinite ease-in-out;
  background: rgba(15, 23, 42, 0.35);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border: 1px solid rgba(99, 102, 241, 0.15);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.05);
  
  /* Space background */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 10% 10%, rgba(99, 102, 241, 0.05) 0%, transparent 30%),
      radial-gradient(circle at 90% 90%, rgba(124, 58, 237, 0.05) 0%, transparent 30%);
    border-radius: inherit;
    z-index: -1;
  }
  
  /* Star-like dots in background */
  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-image: 
      radial-gradient(1px 1px at 5% 10%, rgba(255, 255, 255, 0.7) 1px, transparent 0),
      radial-gradient(1px 1px at 15% 25%, rgba(255, 255, 255, 0.6) 1px, transparent 0),
      radial-gradient(1px 1px at 35% 15%, rgba(255, 255, 255, 0.7) 1px, transparent 0),
      radial-gradient(1px 1px at 65% 5%, rgba(255, 255, 255, 0.6) 1px, transparent 0),
      radial-gradient(1px 1px at 95% 35%, rgba(255, 255, 255, 0.7) 1px, transparent 0);
    border-radius: inherit;
    z-index: -1;
    opacity: 0.3;
  }
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    padding: 1rem;
  }
`;

const StatCard = styled(Card)`
  text-align: center;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: visible;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(148, 163, 184, 0.2);
  position: relative;
  z-index: 1;
  
  /* Space background gradients */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at ${props => props.gradientPos || '30% 70%'}, rgba(99, 102, 241, 0.12) 0%, transparent 50%),
      radial-gradient(circle at ${props => props.gradientPos2 || '70% 30%'}, rgba(124, 58, 237, 0.08) 0%, transparent 50%);
    border-radius: inherit;
    z-index: -1;
    opacity: 0.8;
    transition: opacity 0.4s ease;
  }
  
  /* Star-like dots in background */
  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-image: 
      radial-gradient(1px 1px at 10% 20%, rgba(255, 255, 255, 0.8) 1px, transparent 0),
      radial-gradient(1px 1px at 30% 50%, rgba(255, 255, 255, 0.7) 1px, transparent 0),
      radial-gradient(1px 1px at 50% 30%, rgba(255, 255, 255, 0.8) 1px, transparent 0),
      radial-gradient(1px 1px at 70% 60%, rgba(255, 255, 255, 0.7) 1px, transparent 0),
      radial-gradient(1.5px 1.5px at 90% 40%, rgba(255, 255, 255, 0.8) 1px, transparent 0);
    border-radius: inherit;
    z-index: -1;
    opacity: 0.4;
  }
  
  &:hover {
    transform: translateY(-12px) scale(1.02);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2), 0 0 15px rgba(99, 102, 241, 0.3);
    
    &:before {
      opacity: 1;
    }
  }
`;

const StatValue = styled.div`
  font-size: 2.75rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 0.5rem;
  position: relative;
  animation: ${valuePulse} 4s infinite ease-in-out;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 3px;
    background: linear-gradient(
      to right,
      rgba(99, 102, 241, 0.3),
      rgba(99, 102, 241, 0.8),
      rgba(99, 102, 241, 0.3)
    );
    border-radius: 3px;
    box-shadow: 0 0 8px rgba(99, 102, 241, 0.5);
  }
`;

const StatLabel = styled.div`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 1.25rem;
  font-weight: 500;
  letter-spacing: 0.5px;
`;

const StatisticsCards = ({ stats }) => {
  // Generate random gradient positions for each card to create visual variety
  const gradientPositions = [
    { pos1: '20% 80%', pos2: '80% 20%' },
    { pos1: '30% 60%', pos2: '70% 40%' },
    { pos1: '40% 30%', pos2: '60% 70%' },
    { pos1: '10% 50%', pos2: '90% 30%' }
  ];

  return (
    <StatsContainer>
      {stats.map((stat, index) => (
        <StatCard 
          key={index} 
          gradientPos={gradientPositions[index % gradientPositions.length].pos1}
          gradientPos2={gradientPositions[index % gradientPositions.length].pos2}
        >
          <CardBody>
            <StatValue>{stat.value}</StatValue>
            <StatLabel>{stat.label}</StatLabel>
          </CardBody>
        </StatCard>
      ))}
    </StatsContainer>
  );
};

export default StatisticsCards;
